import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { CheckCircle, Calendar, ExternalLink, Copy, Check, AlertTriangle, Loader2, Link as LinkIcon, Settings, ArrowUpRight, Palette, XCircle, MapPinOff, ShieldCheck, Lock, Eye, EyeOff, Code, Monitor, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { ThemeToggle } from '../../components/ThemeToggle/ThemeToggle';

function SettingsTab({ user, isActive, profile }) {
  var copyState = useState(false);
  var copied = copyState[0];
  var setCopied = copyState[1];
  
  // Password change state
  var [showPasswordSection, setShowPasswordSection] = useState(false);
  var [currentPassword, setCurrentPassword] = useState('');
  var [newPassword, setNewPassword] = useState('');
  var [confirmPassword, setConfirmPassword] = useState('');
  var [showCurrentPw, setShowCurrentPw] = useState(false);
  var [showNewPw, setShowNewPw] = useState(false);
  var [changingPassword, setChangingPassword] = useState(false);
  
  var slugState = useState(null);
  var slug = slugState[0];
  var setSlug = slugState[1];
  
  var generatingState = useState(false);
  var generating = generatingState[0];
  var setGenerating = generatingState[1];
  
  var portalLoadingState = useState(false);
  var portalLoading = portalLoadingState[0];
  var setPortalLoading = portalLoadingState[1];
  
  // Quote settings state
  var blockOutOfAreaState = useState(false);
  var blockOutOfArea = blockOutOfAreaState[0];
  var setBlockOutOfArea = blockOutOfAreaState[1];
  
  var savingQuoteSettingsState = useState(false);
  var savingQuoteSettings = savingQuoteSettingsState[0];
  var setSavingQuoteSettings = savingQuoteSettingsState[1];

  // Timezone state
  var [timezone, setTimezone] = useState('America/Chicago');
  var [savingTimezone, setSavingTimezone] = useState(false);

  // Badge embed state
  var [badgeStyle, setBadgeStyle] = useState('horizontal');
  var [badgeTheme, setBadgeTheme] = useState('light');
  var [badgeCopied, setBadgeCopied] = useState(false);
  var [logoSize, setLogoSize] = useState('medium');
  var [logoTheme, setLogoTheme] = useState('dark');

  useEffect(function() {
    if (profile && profile.profile_slug) {
      setSlug(profile.profile_slug);
    }
    if (profile) {
      setBlockOutOfArea(profile.block_out_of_area_quotes || false);
      setTimezone(profile.timezone || 'America/Chicago');
    }
  }, [profile]);
  
  var handleBlockOutOfAreaChange = async function(checked) {
    setBlockOutOfArea(checked);
    setSavingQuoteSettings(true);
    try {
      await api.updateQuoteSettings({ block_out_of_area_quotes: checked });
      toast.success(checked ? 'Out-of-area quotes will now be blocked' : 'Out-of-area quotes are now allowed');
    } catch (error) {
      // Revert on error
      setBlockOutOfArea(!checked);
      toast.error('Failed to update setting');
    }
    setSavingQuoteSettings(false);
  };

  var handleTimezoneChange = async function(newTimezone) {
    var oldTimezone = timezone;
    setTimezone(newTimezone);
    setSavingTimezone(true);
    try {
      await api.updateProfile({ timezone: newTimezone });
      toast.success('Timezone updated');
    } catch (error) {
      setTimezone(oldTimezone);
      toast.error('Failed to update timezone');
    }
    setSavingTimezone(false);
  };

  var handleChangePassword = async function(e) {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setShowPasswordSection(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
    setChangingPassword(false);
  };

  var handleGenerateSlug = async function() {
    setGenerating(true);
    try {
      var response = await api.generateSlug();
      setSlug(response.data.slug);
      toast.success('Short URL generated!');
    } catch (error) {
      toast.error('Failed to generate short URL');
    }
    setGenerating(false);
  };
  
  var handleManageSubscription = async function() {
    setPortalLoading(true);
    try {
      var response = await api.createBillingPortal();
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Unable to open billing portal. Please contact support.');
      setPortalLoading(false);
    }
  };
  
  var shortUrl = slug ? window.location.origin + '/s/' + slug : null;
  var fullUrl = profile && profile.id ? window.location.origin + '/servers/' + profile.id : null;
  
  var handleCopyUrl = function() {
    var urlToCopy = shortUrl || fullUrl;
    if (urlToCopy) {
      navigator.clipboard.writeText(urlToCopy);
      setCopied(true);
      toast.success('Profile URL copied!');
      setTimeout(function() { setCopied(false); }, 2000);
    }
  };

  var subscriptionPlan = profile ? profile.subscription_plan : null;
  var subscriptionTier = profile ? profile.subscription_pricing_tier : null;
  var subscriptionEnd = profile ? profile.subscription_period_end : null;
  var isFoundingMember = profile ? profile.is_founding_member : false;
  var cancellationScheduled = profile ? profile.cancellation_scheduled : false;
  var cancellationDate = profile ? profile.cancellation_date : null;

  var formatDate = function(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  var getPlanDisplay = function() {
    if (!subscriptionPlan) return 'Subscription Active';
    return subscriptionPlan === 'annual' ? 'Annual Plan' : 'Monthly Plan';
  };
  
  var getTierBadge = function() {
    if (subscriptionTier === 'launch') {
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-teal-500/20 text-teal-400 border border-teal-500/30">Launch Rate</span>;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Appearance */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Palette className="h-4 w-4 text-teal-400" />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400 mb-4">Choose your preferred theme for the dashboard and all pages.</p>
          <ThemeToggle variant="full" />
        </CardContent>
      </Card>

      {/* Quote Settings */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-base text-white flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-teal-400" />
            Quote Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4 p-4 bg-slate-800/60 rounded-lg border border-slate-700">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <MapPinOff className="h-4 w-4 text-amber-400" />
                <span className="font-medium text-white">Block Out-of-Area Quotes</span>
              </div>
              <p className="text-sm text-slate-400">
                When enabled, clients outside your coverage areas will not be able to submit quote requests. 
                They'll still see your profile but will receive a message to contact you directly.
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Note: Clients will always see a warning if their address appears outside your listed coverage, 
                regardless of this setting.
              </p>
            </div>
            <div className="flex items-center gap-2 pt-1">
              {savingQuoteSettings && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              <Switch 
                checked={blockOutOfArea} 
                onCheckedChange={handleBlockOutOfAreaChange}
                disabled={savingQuoteSettings}
                data-testid="block-out-of-area-toggle"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timezone Settings */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Settings className="h-4 w-4 text-teal-400" />
            Regional Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-slate-800/60 rounded-lg border border-slate-700">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-white font-medium">Timezone</Label>
                <p className="text-sm text-slate-400 mt-1">
                  Used for route analytics, ETA calculations, and variance reporting
                </p>
              </div>
              <div className="flex items-center gap-2">
                {savingTimezone && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                <select
                  value={timezone}
                  onChange={(e) => handleTimezoneChange(e.target.value)}
                  disabled={savingTimezone}
                  className="bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  data-testid="timezone-select"
                >
                  <optgroup label="US Timezones">
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Phoenix">Arizona (MST - No DST)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="America/Anchorage">Alaska (AKT)</option>
                    <option value="Pacific/Honolulu">Hawaii (HST)</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="UTC">UTC</option>
                  </optgroup>
                </select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-base text-white">Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-800/60 rounded-lg border border-slate-700">
              <p className="text-sm text-slate-500">Account Email</p>
              <p className="font-medium text-white">{user ? user.email : ''}</p>
            </div>
            <div className="p-4 bg-slate-800/60 rounded-lg border border-slate-700">
              <p className="text-sm text-slate-500">Account Type</p>
              <p className="font-medium text-white">{user ? (user.user_type === 'process_server' ? 'Process Server' : user.user_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())) : ''}</p>
            </div>
          </div>
          
          {/* Password Change Section */}
          <div className="border-t border-slate-700 pt-4">
            {!showPasswordSection ? (
              <Button 
                variant="outline" 
                onClick={() => setShowPasswordSection(true)}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <Lock className="h-4 w-4 mr-2" />
                Change Password
              </Button>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-400">Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showCurrentPw ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="bg-slate-800/60 border-slate-700 text-white pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPw(!showCurrentPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-400">New Password</Label>
                  <div className="relative">
                    <Input
                      type={showNewPw ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-slate-800/60 border-slate-700 text-white pr-10"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw(!showNewPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-400">Confirm New Password</Label>
                  <Input
                    type={showNewPw ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-slate-800/60 border-slate-700 text-white"
                    required
                    minLength={8}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    type="submit" 
                    disabled={changingPassword}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    {changingPassword ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Changing...</>
                    ) : (
                      'Update Password'
                    )}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => {
                      setShowPasswordSection(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="border-slate-700"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Profile URL */}
      {fullUrl && (
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-teal-400" />
              Your Profile URL
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-400 mb-3">Share this link with clients to showcase your profile:</p>
            
            {shortUrl ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 bg-slate-800/60 rounded-lg border border-slate-700 font-mono text-sm text-teal-400">
                  {shortUrl}
                </div>
                <Button 
                  onClick={handleCopyUrl} 
                  variant="outline" 
                  className="border-slate-700 hover:bg-slate-800 shrink-0"
                  title="Copy URL"
                >
                  {copied ? <Check className="h-4 w-4 text-teal-400" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button asChild variant="outline" className="border-slate-700 hover:bg-slate-800 shrink-0" title="Open profile">
                  <a href={shortUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-3 bg-slate-800/60 rounded-lg border border-slate-700 font-mono text-xs text-slate-500 truncate">
                    {fullUrl}
                  </div>
                  <Button 
                    onClick={handleCopyUrl} 
                    variant="outline" 
                    className="border-slate-700 hover:bg-slate-800 shrink-0"
                    title="Copy URL"
                  >
                    {copied ? <Check className="h-4 w-4 text-teal-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <Button 
                  onClick={handleGenerateSlug}
                  disabled={generating}
                  variant="outline" 
                  className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10"
                >
                  {generating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                  ) : (
                    <><LinkIcon className="h-4 w-4 mr-2" />Generate Short URL</>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subscription Status */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-base text-white">Subscription</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isActive ? (
            <>
              {/* Cancellation Pending Warning */}
              {cancellationScheduled && (
                <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30 mb-4">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-amber-400">Cancellation Scheduled</p>
                      <p className="text-sm text-amber-400/80 mt-1">
                        Your subscription will end on {formatDate(cancellationDate)}. Your profile will remain visible until then.
                      </p>
                      <p className="text-sm text-slate-400 mt-2">
                        Changed your mind? Click "Manage" below to reactivate your subscription and keep your profile listed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="p-4 bg-teal-500/10 rounded-lg border border-teal-500/30">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-teal-400">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-semibold text-lg">{getPlanDisplay()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getTierBadge()}
                    {isFoundingMember && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                        Founding Member
                      </span>
                    )}
                  </div>
                </div>
                {subscriptionEnd && (
                  <p className="text-sm text-teal-400/70 mt-2 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {cancellationScheduled 
                      ? 'Ends' 
                      : (subscriptionPlan === 'annual' ? 'Renews' : 'Next billing')}: {formatDate(subscriptionEnd)}
                  </p>
                )}
              </div>

              <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-700/50">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h4 className="font-medium text-white mb-1">Manage Subscription</h4>
                    <p className="text-sm text-slate-400">
                      Upgrade, downgrade, update payment method, or cancel
                    </p>
                  </div>
                  <Button 
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    className="btn-secondary"
                  >
                    {portalLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading...</>
                    ) : (
                      <><Settings className="h-4 w-4 mr-2" />Manage<ArrowUpRight className="h-4 w-4 ml-1" /></>
                    )}
                  </Button>
                </div>
              </div>
              
              <p className="text-xs text-slate-500">
                Downgrades take effect at the end of your current billing period. Your locked-in rate is preserved.
              </p>
            </>
          ) : (
            <>
              <div className="p-4 bg-slate-800/60 rounded-lg border border-slate-700">
                <div className="flex items-center gap-2 text-slate-400">
                  <AlertTriangle className="h-5 w-5 text-amber-400" />
                  <span className="font-medium">No Active Subscription</span>
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Subscribe to appear in search results and receive quote requests.
                </p>
              </div>
              <Button asChild className="w-full btn-accent">
                <Link to="/pricing#plans">View Plans & Subscribe</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Embeddable Badge */}
      {profile && (profile.profile_slug || profile.id) && (
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Code className="w-4 h-4" />
              Embeddable Badge
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-400">
              Add a badge to your website to showcase your Servio profile and verifications.
            </p>
            
            {/* Simple Logo Badge Section */}
            <div className="p-4 rounded-lg border border-slate-700 bg-slate-800/50 space-y-4">
              <div>
                <Label className="text-slate-300">Simple Logo Badge</Label>
                <p className="text-xs text-slate-500 mt-1">The Servio logo with a <span className="text-teal-400">transparent background</span> - works on any website.</p>
              </div>
              
              {/* Size Selector */}
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">Size</Label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { value: 'small', label: 'Small (30px)' },
                    { value: 'medium', label: 'Medium (50px)' },
                    { value: 'large', label: 'Large (80px)' },
                    { value: 'xlarge', label: 'XL (120px)' },
                  ].map((size) => (
                    <Button
                      key={size.value}
                      variant={logoSize === size.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setLogoSize(size.value)}
                      className={logoSize === size.value ? 'btn-accent' : ''}
                    >
                      {size.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Logo Color Selector */}
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">Logo Color</Label>
                <p className="text-xs text-slate-500">Choose based on your website's background color</p>
                <div className="flex gap-2">
                  <Button
                    variant={logoTheme === 'dark' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setLogoTheme('dark')}
                    className={logoTheme === 'dark' ? 'btn-accent' : ''}
                  >
                    Dark Logo (for light sites)
                  </Button>
                  <Button
                    variant={logoTheme === 'light' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setLogoTheme('light')}
                    className={logoTheme === 'light' ? 'btn-accent' : ''}
                  >
                    Light Logo (for dark sites)
                  </Button>
                </div>
              </div>
              
              {/* Preview */}
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">Preview <span className="text-slate-600 font-normal">(background is just for visibility)</span></Label>
                <div className={`p-6 rounded-lg flex items-center justify-center ${logoTheme === 'dark' ? 'bg-white border border-slate-300' : 'bg-slate-900'}`} style={{backgroundImage: logoTheme === 'dark' ? 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)' : 'none', backgroundSize: '20px 20px', backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'}}>
                  <img 
                    key={`${logoTheme}-${logoSize}`}
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/badge/${logoTheme === 'dark' ? 'logo-light' : 'logo'}/${logoSize}?v=2`} 
                    alt="Servio Logo"
                  />
                </div>
                <p className="text-xs text-slate-600 italic">The checkered pattern shows the logo has a transparent background</p>
              </div>
              
              {/* Copy Code Button */}
              <Button
                variant="outline"
                onClick={() => {
                  const endpoint = logoTheme === 'dark' ? 'logo-light' : 'logo';
                  const code = `<a href="https://servio.legal/servers/${profile.profile_slug || profile.id}" target="_blank" rel="noopener noreferrer"><img src="https://servio.legal/api/badge/${endpoint}/${logoSize}?v=2" alt="Verified on Servio"></a>`;
                  navigator.clipboard.writeText(code);
                  toast.success('Logo embed code copied!');
                }}
                className="w-full"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Embed Code
              </Button>
            </div>

            <div className="border-t border-slate-700 pt-4">
              <Label className="text-slate-300 mb-3 block">Profile Card Badges</Label>
            </div>
            
            {/* Style Selector */}
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Card Style</Label>
              <div className="flex gap-2">
                <Button
                  variant={badgeStyle === 'horizontal' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setBadgeStyle('horizontal')}
                  className={badgeStyle === 'horizontal' ? 'btn-accent' : ''}
                >
                  <Monitor className="w-3 h-3 mr-1" />
                  Horizontal
                </Button>
                <Button
                  variant={badgeStyle === 'vertical' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setBadgeStyle('vertical')}
                  className={badgeStyle === 'vertical' ? 'btn-accent' : ''}
                >
                  <Smartphone className="w-3 h-3 mr-1" />
                  Vertical
                </Button>
                <Button
                  variant={badgeStyle === 'compact' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setBadgeStyle('compact')}
                  className={badgeStyle === 'compact' ? 'btn-accent' : ''}
                >
                  Compact
                </Button>
              </div>
            </div>
            
            {/* Theme Selector */}
            <div className="space-y-2">
              <Label className="text-slate-300">Theme</Label>
              <div className="flex gap-2">
                <Button
                  variant={badgeTheme === 'light' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setBadgeTheme('light')}
                  className={badgeTheme === 'light' ? 'btn-accent' : ''}
                >
                  Light
                </Button>
                <Button
                  variant={badgeTheme === 'dark' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setBadgeTheme('dark')}
                  className={badgeTheme === 'dark' ? 'btn-accent' : ''}
                >
                  Dark
                </Button>
              </div>
            </div>
            
            {/* Preview */}
            <div className="space-y-2">
              <Label className="text-slate-300">Preview</Label>
              <div className={`p-4 rounded-lg border ${badgeTheme === 'dark' ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                <iframe
                  key={`${badgeStyle}-${badgeTheme}`}
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/badge/${profile.profile_slug || profile.id}?style=${badgeStyle}&theme=${badgeTheme}`}
                  style={{
                    border: 'none',
                    width: badgeStyle === 'compact' ? '280px' : badgeStyle === 'vertical' ? '240px' : '450px',
                    height: badgeStyle === 'compact' ? '60px' : badgeStyle === 'vertical' ? '320px' : '120px',
                  }}
                  title="Badge Preview"
                />
              </div>
            </div>
            
            {/* Embed Code */}
            <div className="space-y-2">
              <Label className="text-slate-300">Embed Code</Label>
              <div className="relative">
                <pre className="bg-slate-900 p-3 rounded-lg text-xs text-slate-300 overflow-x-auto">
                  {`<iframe src="https://servio.legal/api/badge/${profile.profile_slug || profile.id}?style=${badgeStyle}&theme=${badgeTheme}" style="border:none;width:${badgeStyle === 'compact' ? '280px' : badgeStyle === 'vertical' ? '240px' : '450px'};height:${badgeStyle === 'compact' ? '60px' : badgeStyle === 'vertical' ? '320px' : '120px'};" title="Servio Process Server Badge"></iframe>`}
                </pre>
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-2 right-2"
                  onClick={() => {
                    const embedCode = `<iframe src="https://servio.legal/api/badge/${profile.profile_slug || profile.id}?style=${badgeStyle}&theme=${badgeTheme}" style="border:none;width:${badgeStyle === 'compact' ? '280px' : badgeStyle === 'vertical' ? '240px' : '450px'};height:${badgeStyle === 'compact' ? '60px' : badgeStyle === 'vertical' ? '320px' : '120px'};" title="Servio Process Server Badge"></iframe>`;
                    navigator.clipboard.writeText(embedCode);
                    setBadgeCopied(true);
                    toast.success('Embed code copied!');
                    setTimeout(() => setBadgeCopied(false), 2000);
                  }}
                >
                  {badgeCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data & Privacy */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-base text-white">Data & Privacy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-400">
            Your data is protected according to our privacy policy. You have the right to request a copy of your data or request deletion.
          </p>
          <div className="flex gap-3">
            <Link to="/privacy" className="text-sm text-teal-400 hover:text-teal-300">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-sm text-teal-400 hover:text-teal-300">
              Terms of Service
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default SettingsTab;
